import {
  Box,
  Button,
  Flex,
  Form,
  SortableTable,
  SubmitButton,
  type TableColumnType,
} from '@/components';
import { useChangeOrderOfAccountingType } from '@/features/master-table/hooks';
import type {
  AccountingTypeModel,
  AccountingTypesModel,
} from '@/features/master-table/models';
import { useSortOrder } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  dataSource: z.infer<typeof AccountingTypesModel>;
  columns: Array<TableColumnType<z.infer<typeof AccountingTypeModel>>>;
  onCancel: () => void;
  onSubmitSuccess: () => void;
};

export const AccountingTypeSortableTable: FC<Props> = ({
  dataSource,
  columns,
  onCancel,
  onSubmitSuccess,
}) => {
  const { sortedData, onDragEnd, hasDataIndexChanged } =
    useSortOrder<z.infer<typeof AccountingTypeModel>>(dataSource);
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { mutateAsync: updateSortOrderAsync } =
    useChangeOrderOfAccountingType();

  const onSubmit = async () => {
    const params = sortedData.map((d, i) => ({
      id: d.id,
      sortOrder: i + 1,
    }));

    await updateSortOrderAsync(params);
    await Promise.all([
      openMessage('success', t('message-update-completed')),
      (async () => {
        onSubmitSuccess();
      })(),
    ]);
  };

  return (
    <Form
      leavePageAlertCondition={{
        additionalAlertCondition: hasDataIndexChanged,
      }}
      onFinish={onSubmit}
    >
      <Box marginBottom={12}>
        <Flex justify="flex-end" gap={4}>
          <Button onClick={onCancel}>{t('cancel')}</Button>
          <SubmitButton text={t('save')} disabled={!hasDataIndexChanged} />
        </Flex>
      </Box>

      <SortableTable<z.infer<typeof AccountingTypesModel>>
        dataSource={sortedData}
        columns={columns}
        rowKey="id"
        rowKeys={sortedData.map((d) => d.id)}
        onDragEnd={onDragEnd}
        pagination={false}
      />
    </Form>
  );
};
